import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {AppConfigService} from '../common/service/app-config.service';
import {catchError, Observable, of, takeWhile, tap, throwError, timer} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {PlanModelService} from '../common/service/plan-model.service';
import {PlanPdfService} from '../common/service/plan-pdf.service';
import {
    DigitalPlanAttachmentVirusScanStatusResponse,
    DigitalPlanPdfRequest,
    DigitalPlanPresignedUrlResponse,
    DigitalPlanValidationResponse,
    DigitalPlanValidationStatusResponse
} from './digital-plan.model';
import {HTTP_STATUS_CODES, PLAN_LODGEMENT_STAGE, XMLHTTP_REQUEST_READY_STATE} from '../common/constants';
import {AuthenticatorService} from '../common/service/authenticator.service';
// TODO WADPL-1128 TEMP override stack - remove with common/fme-stack.ts.
import {fmeStackQuery} from '../common/fme-stack';

@Injectable({
    providedIn: 'root'
})
export class DigitalPlanService {
    title = 'Digital Plan Service';

    digitalPlanValidationPollInterval = 5000;

    constructor(
        private http: HttpClient,
        private appConfigService: AppConfigService,
        private authenticatorService: AuthenticatorService,
        private planModelService: PlanModelService,
        private planPdfService: PlanPdfService
    ) {}

    uploadDwg(dwgFile: File) {
        console.log('uploadDwg');

        return new Observable(observer => {
            const fileReader = new FileReader();

            fileReader.onload = () => {
                observer.next(fileReader.result as ArrayBuffer);
                observer.complete();
            };

            fileReader.onerror = error => {
                observer.error(error);
            };

            fileReader.readAsArrayBuffer(dwgFile);
        }).pipe(
            switchMap((fileArrayBuffer: ArrayBuffer) => {
                const url = `${this.appConfigService.settings.apiGatewayUrl}/digital-plan/presigned-upload-url/${dwgFile.size}`;
                return this.http.get<DigitalPlanPresignedUrlResponse>(url).pipe(
                    catchError(error => {
                        console.error('uploadDwg - System error getting signed url:', error);
                        return throwError(() => new Error('System error getting signed url'));
                    }),
                    switchMap(response => {
                        const xhr = new XMLHttpRequest();
                        return new Observable(observer => {
                            xhr.onreadystatechange = () => {
                                if (xhr.readyState === XMLHTTP_REQUEST_READY_STATE.DONE) {
                                    if (xhr.status === HTTP_STATUS_CODES.OK) {
                                        observer.next(response.key);
                                        observer.complete();
                                    } else {
                                        console.error('uploadDwg - The file did not upload - xhr.status:', xhr.status);
                                        observer.error('The file did not upload');
                                    }
                                }
                            };
                            xhr.open('PUT', response.url, true);
                            xhr.setRequestHeader('Content-Type', 'image/vnd.dwg');
                            xhr.setRequestHeader('x-amz-server-side-encryption', 'AES256');
                            xhr.setRequestHeader('x-amz-meta-user-firm', `${this.authenticatorService.getUsersFirmNumber()}`);
                            xhr.send(fileArrayBuffer);

                            // Abort the upload if unsubscribed before it completes, e.g. when the upload times out
                            return () => {
                                if (xhr.readyState !== XMLHTTP_REQUEST_READY_STATE.DONE) {
                                    xhr.onreadystatechange = null;
                                    xhr.abort();
                                }
                            };
                        });
                    }),
                    switchMap((fileLocation: string) => {
                        return this.getAttachmentVirusScanStatus(fileLocation).pipe(
                            catchError(error => {
                                console.error(`uploadDwg - error getting virus scan status for ${fileLocation}:`, error);
                                return throwError(() => new Error('Unable to get virus scan status'));
                            })
                        );
                    })
                );
            })
        );
    }

    getAttachmentVirusScanStatus(fileLocation: string) {
        const url = `${this.appConfigService.settings.apiGatewayUrl}/digital-plan/attachment/virus-scan-status?fileLocation=${fileLocation}`;
        return this.http.get<DigitalPlanAttachmentVirusScanStatusResponse>(url);
    }

    isValidationComplete(digitalPlanValidationStatusResponse: DigitalPlanValidationStatusResponse) {
        return ['Success', 'Failure', 'Cancelled'].includes(digitalPlanValidationStatusResponse?.status);
    }

    isValidationInProgress(digitalPlanValidationStatusResponse: DigitalPlanValidationStatusResponse) {
        return digitalPlanValidationStatusResponse && !this.isValidationComplete(digitalPlanValidationStatusResponse);
    }

    getValidation(): Observable<DigitalPlanValidationStatusResponse> {
        return this.getDigitalPlanValidation().pipe(
            switchMap(response => {
                if (!response) {
                    return of(null);
                }
                if (this.isValidationComplete(response)) {
                    return of(response);
                }
                return timer(0, this.digitalPlanValidationPollInterval).pipe(
                    switchMap(() => this.digitalPlanValidationPoll(response.correlationId)),
                    takeWhile(statusResponse => !['Success', 'Failure', 'Cancelled'].includes(statusResponse.status), true)
                );
            })
        );
    }

    /**
     * Runs the digital plan validation.
     * @param reuse false (the default/base case) runs a full validation; true reuses the existing full validation and
     *        re-runs only the FME status=LODGE validation subset. Used when progressing the plan to Ready for Certification.
     *        The backend falls back to a full validation when there is no prior validation to reuse or any of the reused
     *        jobs did not previously succeed.
     */
    runValidation(reuse = false): Observable<DigitalPlanValidationStatusResponse> {
        return this.createDigitalPlanValidation(reuse).pipe(
            switchMap(response =>
                timer(0, this.digitalPlanValidationPollInterval).pipe(
                    switchMap(() => this.digitalPlanValidationPoll(response.correlationId)),
                    takeWhile(statusResponse => !['Success', 'Failure', 'Cancelled'].includes(statusResponse.status), true)
                )
            )
        );
    }

    print(request: Partial<DigitalPlanPdfRequest>) {
        // Only save the plan before generating the PDF if the plan has not been lodged. Saving a lodged plan is invalid and will result in an error
        return (this.planModelService.getModel().planLodgementStage === PLAN_LODGEMENT_STAGE.LODGED ? of(null) : this.planModelService.save()).pipe(
            switchMap(() => {
                return this.http.post(
                    `${this.appConfigService.settings.apiGatewayUrl}/digital-plan/pdf`,
                    {...request, planLodgementId: this.planModelService.getModel().id},
                    {responseType: 'arraybuffer'}
                );
            })
        );
    }

    printAndDisplay(watermarkText: string) {
        const title = this.planPdfService.buildPdfTitle(this.planModelService.getModel());
        return this.print({watermarkText}).pipe(tap((res: ArrayBuffer) => this.planPdfService.displayPdf(title, res)));
    }

    cancelValidation(correlationId: string): Observable<DigitalPlanValidationResponse> {
        // TODO WADPL-1128 TEMP override stack - drop the fmeStackQuery() call. The cancel must reach the same FME stack the
        // validation was submitted to, so it carries the same page URL parameter.
        const url = `${this.appConfigService.settings.apiGatewayUrl}/digital-plan/validation/cancel/${correlationId}${fmeStackQuery('?')}`;
        return this.http.post<DigitalPlanValidationStatusResponse>(url, null);
    }

    /**
     * Cancels the latest digital plan validation for the current plan, if one exists and is not already cancelled.
     * @returns the cancelled validation, or null when there was nothing to cancel.
     */
    cancelLatestValidation(): Observable<DigitalPlanValidationResponse | null> {
        return this.getDigitalPlanValidation().pipe(
            switchMap(validation => {
                if (validation && validation.status !== 'Cancelled') {
                    return this.cancelValidation(validation.correlationId);
                }
                return of(null);
            })
        );
    }

    private createDigitalPlanValidation(reuse: boolean): Observable<DigitalPlanValidationResponse> {
        const planLodgementId = this.planModelService.getModel().id;
        // TODO WADPL-1128 TEMP override stack - drop the fmeStackQuery() call.
        const url = `${this.appConfigService.settings.apiGatewayUrl}/digital-plan/validation/${planLodgementId}?reuse=${reuse}${fmeStackQuery('&')}`;
        return this.http.post<DigitalPlanValidationResponse>(url, null);
    }

    private getDigitalPlanValidation(): Observable<DigitalPlanValidationStatusResponse> {
        const planLodgementId = this.planModelService.getModel().id;
        if (planLodgementId) {
            const url = `${this.appConfigService.settings.apiGatewayUrl}/digital-plan/validation/${planLodgementId}`;
            return this.http.get<DigitalPlanValidationStatusResponse>(url);
        }
        return of(null);
    }

    private digitalPlanValidationPoll(correlationId: string): Observable<DigitalPlanValidationStatusResponse> {
        const url = `${this.appConfigService.settings.apiGatewayUrl}/digital-plan/validation/poll/${correlationId}`;
        return this.http.get<DigitalPlanValidationStatusResponse>(url);
    }
}
